/**
 * FilterSidebar.tsx
 *
 * FilterSidebar component for the Marketplace UI.
 * Provides filtering options for solutions by author, type, integrated solutions, and categories.
 * Includes search, multi-select, and responsive UI features.
 *
 * Features:
 * - Author, type, integration, and category filters
 * - Category search and show more/less
 * - Responsive and accessible UI
 *
 * @module FilterSidebar
 */
import React, { useRef, useEffect, useState, useMemo } from 'react';
import { Icon } from '@ellucian/ds-icons/lib';
import { Typography, Checkbox, Grid, TextLink } from '@ellucian/react-design-system/core';
import { trackFilter } from '../../utils/analytics';

/**
 * Props for the FilterSidebar component.
 * @property createdByOptions - List of available authors for filtering
 * @property tagList - List of available tags/categories for filtering
 * @property typeList - List of available solution types for filtering
 * @property integratedSolutions - List of available integrated solutions for filtering
 * @property selectedAuthors - Currently selected authors
 * @property setSelectedAuthors - Callback to update selected authors
 * @property selectedTypes - Currently selected types
 * @property setSelectedTypes - Callback to update selected types
 * @property selectedIntegratedSolutions - Currently selected integrated solutions
 * @property setSelectedIntegratedSolutions - Callback to update selected integrated solutions
 * @property selectedCategories - Currently selected categories
 * @property setSelectedCategories - Callback to update selected categories
 * @property isLoggedIn - Whether the user is currently logged in
 * @property showOnlyEntitledSolutions - Whether to show only entitled solutions
 * @property setShowOnlyEntitledSolutions - Callback to update entitled filter
 */
type FilterCardProps = {
  createdByOptions: string[];
  tagList: string[];
  typeList: string[];
  integratedSolutions: string[];
  selectedAuthors: string[];
  setSelectedAuthors: (authors: string[]) => void;
  selectedTypes: string[];
  setSelectedTypes: (types: string[]) => void;
  selectedIntegratedSolutions: string[];
  setSelectedIntegratedSolutions: (solutions: string[]) => void;
  selectedCategories?: string[];
  setSelectedCategories?: (categories: string[]) => void;
  isLoggedIn?: boolean;
  showOnlyEntitledSolutions?: boolean;
  setShowOnlyEntitledSolutions?: (show: boolean) => void;
  showCoreEligibleOnly?: boolean;
  setShowCoreEligibleOnly?: (value: boolean) => void;
  deliveryModelOptions?: { label: string; value: string }[];
  selectedDeliveryModels?: string[];
  setSelectedDeliveryModels?: (models: string[]) => void;
  showAdditionalPurchaseOnly?: boolean;
  setShowAdditionalPurchaseOnly?: (value: boolean) => void;
};


/**
 * FilterSidebar component
 * Renders filter sections for author, type, integrated solutions, and categories.
 * Includes category search, show more/less, and responsive UI.
 *
 * @param {FilterCardProps} props - Component props
 * @returns {JSX.Element}
 */
const FilterSidebar: React.FC<FilterCardProps> = ({
  createdByOptions,
  tagList,
  typeList,
  integratedSolutions,
  selectedAuthors,
  setSelectedAuthors,
  selectedTypes,
  setSelectedTypes,
  selectedIntegratedSolutions,
  setSelectedIntegratedSolutions,
  selectedCategories = [],
  setSelectedCategories = () => { },
  isLoggedIn = false,
  showOnlyEntitledSolutions = false,
  setShowOnlyEntitledSolutions = () => { },
  showCoreEligibleOnly = false,
  setShowCoreEligibleOnly = () => { },
  deliveryModelOptions = [],
  selectedDeliveryModels = [],
  setSelectedDeliveryModels = () => { },
  showAdditionalPurchaseOnly = false,
  setShowAdditionalPurchaseOnly = () => { },
}) => {
  // --- State ---
  /** Ref for dropdown (future use, e.g., closing on outside click) */
  const dropdownRef = useRef<HTMLDivElement>(null);
  /** Category search input value */
  const [categorySearchQuery, setCategorySearchQuery] = useState('');
  /** Filtered categories based on search and selection */
  const [filteredCategories, setFilteredCategories] = useState(tagList);
  /** Toggle showing all categories or a limited set */
  const [showAllCategories, setShowAllCategories] = useState(false);

  // --- Effects ---
  // (Dropdown outside click effect is a placeholder for future dropdowns)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current &&
        dropdownRef.current.contains &&
        !dropdownRef.current.contains(event.target as Node)) {
        // Placeholder for dropdown close logic
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter and sort categories based on search query and selection
  useEffect(() => {
    let filtered = tagList;
    if (categorySearchQuery.trim() !== '') {
      filtered = tagList.filter(category =>
        category.toLowerCase().includes(categorySearchQuery.toLowerCase())
      );
    }
    // Sort: selected categories first, then alphabetically
    const sortedCategories = [...filtered].sort((a, b) => {
      const aSelected = selectedCategories.includes(a);
      const bSelected = selectedCategories.includes(b);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return a.localeCompare(b);
    });
    setFilteredCategories(sortedCategories);
  }, [categorySearchQuery, tagList, selectedCategories]);

  // --- Handlers ---
  const displayedAuthors = useMemo(() => {
    return [...createdByOptions];
  }, [createdByOptions]);
  /** Handles changes to the category search input. */
  const handleCategorySearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCategorySearchQuery(e.target.value);
  };

  /** Toggles selection of an author in the filter. */
  const handleAuthorChange = (name: string) => {
    const isAdding = !selectedAuthors.includes(name);
    if (selectedAuthors.includes(name)) {
      setSelectedAuthors(selectedAuthors.filter((a) => a !== name));
    } else {
      setSelectedAuthors([...selectedAuthors, name]);
    }
    // Track filter action
    trackFilter('author', isAdding ? `add_${name}` : `remove_${name}`);
  };

  /** Toggles selection of a solution type in the filter. */
  const handleTypeChange = (type: string) => {
    const isAdding = !selectedTypes.includes(type);
    if (selectedTypes.includes(type)) {
      setSelectedTypes(selectedTypes.filter((t) => t !== type));
    } else {
      setSelectedTypes([...selectedTypes, type]);
    }
    // Track filter action
    trackFilter('type', isAdding ? `add_${type}` : `remove_${type}`);
  };

  /** Toggles selection of an integrated solution in the filter. */
  const handleIntegratedSolutionChange = (solution: string) => {
    const isAdding = !selectedIntegratedSolutions.includes(solution);
    if (selectedIntegratedSolutions.includes(solution)) {
      setSelectedIntegratedSolutions(selectedIntegratedSolutions.filter((s) => s !== solution));
    } else {
      setSelectedIntegratedSolutions([...selectedIntegratedSolutions, solution]);
    }
    // Track filter action
    trackFilter('integrated_solution', isAdding ? `add_${solution}` : `remove_${solution}`);
  };

  /** Toggles selection of a delivery model in the filter. */
  const handleDeliveryModelChange = (value: string) => {
    const isAdding = !selectedDeliveryModels.includes(value);
    if (selectedDeliveryModels.includes(value)) {
      setSelectedDeliveryModels(selectedDeliveryModels.filter((m) => m !== value));
    } else {
      setSelectedDeliveryModels([...selectedDeliveryModels, value]);
    }
    trackFilter('delivery_model', isAdding ? `add_${value}` : `remove_${value}`);
  };

  // --- Main render: filter sections for author, type, integration, and categories ---
  return (
    <div
      style={{
        height: '100vh',
        overflowY: 'auto',
        overflowX: 'hidden',
        boxSizing: 'border-box',
        paddingBottom: '32px',
        paddingRight: '8px', 
      }}
      className="filter-sidebar-scrollable"
    >
      <style>{`
        /* Responsive placeholder sizing for category search input (safe, no JSX changes) */
        .filter-sidebar-scrollable input[placeholder="Search by category"]::placeholder {
          font-size: 14px;
          color: #9CA3AF;
          opacity: 1;
        }
        @media (max-width: 1024px) {
          .filter-sidebar-scrollable input[placeholder="Search by category"]::placeholder { font-size: 13px; }
          .filter-sidebar-scrollable input[placeholder="Search by category"] { font-size: 13px; }
        }
        @media (max-width: 768px) {
          .filter-sidebar-scrollable input[placeholder="Search by category"]::placeholder { font-size: 12px; }
          .filter-sidebar-scrollable input[placeholder="Search by category"] { font-size: 12px; }
        }

        .category-search-form { max-width: 250px; }
        @media (max-width: 1200px) { .category-search-form { max-width: 220px; } }
        @media (max-width: 1024px) { .category-search-form { max-width: 200px; } }
        @media (max-width: 768px) { .category-search-form { max-width: 160px; } }

        /* Phones: make search input fuller and easier to tap/read */
        @media (max-width: 480px) {
          .category-search-form { width: 100%; max-width: none; }
          .filter-sidebar-scrollable input[placeholder="Search by category"],
          .category-search-form input {
            font-size: 14px;
            height: 44px;
            padding-left: 12px;
            padding-right: 40px; /* room for icon */
          }
          .category-search-form { border-radius: 6px; }
        }

        .filter-sidebar-scrollable::-webkit-scrollbar {
          width: 8px;
        }
        .filter-sidebar-scrollable::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        .filter-sidebar-scrollable::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 4px;
        }
        .filter-sidebar-scrollable::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
        .filter-sidebar-scrollable {
          scrollbar-width: thin;
          scrollbar-color: #c1c1c1 #f1f1f1;
        }
      `}</style>
      <Grid container direction="column" spacing={4} style={{ flexShrink: 0, marginTop: 0, paddingTop: 0 }}>

       

        {/* Author filter section */}
        <Grid item style={{ marginTop: 16, paddingTop: 0 }}>
          <Typography
            variant="body2"
            style={{
              color: 'var(--Neutral-neutral-600, #151618)',
              fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 700,
              lineHeight: '125%', // 20px
              letterSpacing: '0.5px',
              textAlign: 'left',
              marginBottom: 16,
              marginLeft: 0,
              paddingLeft: 0,
            }}
          >
            Created by
          </Typography>
          <Grid container direction="column" spacing={2} style={{ width: '100%' }}>
            {displayedAuthors.map((name) => (
              <Grid item key={name}>
                <Grid container alignItems="center" spacing={1} wrap="nowrap">
                  <Grid item>
                    <Checkbox
                      checked={selectedAuthors.includes(name)}
                      onChange={() => handleAuthorChange(name)}
                      value={name}
                      inputProps={{ 'aria-label': name }}
                      style={{ margin: 0, width: 20, height: 20, minWidth: 20, minHeight: 20 }}
                    />
                  </Grid>
                  <Grid item>
                    <span
                      style={{
                        color: 'var(--Neutral-neutral-600, #151618)',
                        fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
                        fontSize: '14px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '20px',
                        letterSpacing: '0.07px',
                        margin: 0,
                        marginTop: '1px',
                        padding: 0,
                      }}
                    >
                      {name}
                    </span>
                  </Grid>
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Cost filter section (login only) */}
        {isLoggedIn && (
          <Grid item style={{ width: '100%' }}>
            <Typography
              variant="body2"
              style={{
                color: 'var(--Neutral-neutral-600, #151618)',
                fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: '125%',
                letterSpacing: '0.5px',
                textAlign: 'left',
                marginBottom: 16,
                marginLeft: 0,
                paddingLeft: 0,
              }}
            >
              Cost
            </Typography>
            <Grid container direction="column" spacing={2} style={{ width: '100%' }}>
              <Grid item>
                <Grid container alignItems="center" spacing={1} wrap="nowrap">
                  <Grid item>
                    <Checkbox
                      checked={showAdditionalPurchaseOnly}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setShowAdditionalPurchaseOnly(e.target.checked);
                        trackFilter('cost', e.target.checked ? 'add_additional_purchase' : 'remove_additional_purchase');
                      }}
                      value="additional_purchase"
                      inputProps={{ 'aria-label': 'Additional purchase' }}
                      style={{ margin: 0, width: 20, height: 20, minWidth: 20, minHeight: 20 }}
                    />
                  </Grid>
                  <Grid item>
                    <span
                      style={{
                        color: 'var(--Neutral-neutral-600, #151618)',
                        fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
                        fontSize: '14px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '20px',
                        letterSpacing: '0.07px',
                        margin: 0,
                        marginTop: '1px',
                        padding: 0,
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        const next = !showAdditionalPurchaseOnly;
                        setShowAdditionalPurchaseOnly(next);
                        trackFilter('cost', next ? 'add_additional_purchase' : 'remove_additional_purchase');
                      }}
                    >
                      Additional purchase
                    </span>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}

        {/* Availability filter section (show if logged in OR if core eligibility filter exists) */}
        {isLoggedIn && (
          <Grid item style={{ marginTop: 16, paddingTop: 0 }}>
            <Typography
              variant="body2"
              style={{
                color: 'var(--Neutral-neutral-600, #151618)',
                fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: '125%',
                letterSpacing: '0.5px',
                textAlign: 'left',
                marginBottom: 16,
                marginLeft: 0,
              }}
            >
              Availability
            </Typography>
            <Grid container direction="column" spacing={1} style={{ width: '100%' }}>
              {/* Entitled filter (only when logged in) */}
              {isLoggedIn && (
                <Grid item>
                  <Grid container alignItems="center" spacing={1} wrap="nowrap">
                    <Grid item>
                      <Checkbox
                        checked={showOnlyEntitledSolutions}
                        onChange={() => setShowOnlyEntitledSolutions(!showOnlyEntitledSolutions)}
                        inputProps={{ 'aria-label': 'Entitled' }}
                        style={{ margin: 0, width: 20, height: 20, minWidth: 20, minHeight: 20 }}
                      />
                    </Grid>
                    <Grid item style={{ flex: 1, minWidth: 0 }}>
                      <span
                        style={{
                          color: 'var(--Neutral-neutral-600, #151618)',
                          fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
                          fontSize: '14px',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          lineHeight: '20px',
                          letterSpacing: '0.07px',
                          margin: 0,
                          padding: 0,
                          cursor: 'pointer',
                          display: 'block',
                        }}
                        onClick={() => setShowOnlyEntitledSolutions(!showOnlyEntitledSolutions)}
                      >
                        Entitled
                      </span>
                    </Grid>
                  </Grid>
                </Grid>
              )}
              {/* Core eligible filter */}
              <Grid item>
                <Grid container alignItems="center" spacing={1} wrap="nowrap">
                  <Grid item>
                    <Checkbox
                      checked={showCoreEligibleOnly || false}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setShowCoreEligibleOnly?.(e.target.checked);
                        trackFilter('core_selection', e.target.checked ? 'add' : 'remove');
                      }}
                      value="core_selection"
                      inputProps={{ 'aria-label': 'Available for core selection' }}
                      style={{ margin: 0, width: 20, height: 20, minWidth: 20, minHeight: 20 }}
                    />
                  </Grid>
                  <Grid item style={{ flex: 1, minWidth: 0 }}>
                    <span
                      style={{
                        color: 'var(--Neutral-neutral-600, #151618)',
                        fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
                        fontSize: '14px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '20px',
                        letterSpacing: '0.07px',
                        margin: 0,
                        padding: 0,
                        cursor: 'pointer',
                        display: 'block',
                      }}
                      onClick={() => {
                        const next = !showCoreEligibleOnly;
                        setShowCoreEligibleOnly?.(next);
                        trackFilter('core_selection', next ? 'add' : 'remove');
                      }}
                    >
                      Available for core selection
                    </span>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}

        {/* Type filter section */}
        <Grid item style={{ width: '100%' }}>
          <Typography
            variant="body2"
            style={{
              color: 'var(--Neutral-neutral-600, #151618)',
              fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 700,
              lineHeight: '125%', // 20px
              letterSpacing: '0.5px',
              textAlign: 'left',
              marginBottom: 16,
              marginLeft: 0,
              paddingLeft: 0,
            }}
          >
            Type
          </Typography>
          <Grid container direction="column" spacing={2} style={{ width: '100%' }}>
            {typeList.map((type) => {
              const isExtension = type === 'Extension';
              return (
                <Grid item key={type}>
                  <Grid container alignItems="center" spacing={1} wrap="nowrap">
                    <Grid item>
                      <Checkbox
                        checked={selectedTypes.includes(type)}
                        onChange={() => handleTypeChange(type)}
                        value={type}
                        inputProps={{ 'aria-label': type }}
                        disabled={isExtension}
                        style={{
                          margin: 0,
                          width: 20,
                          height: 20,
                          minWidth: 20,
                          minHeight: 20,
                          color: isExtension ? '#BDBDBD' : undefined,
                        }}
                      />
                    </Grid>
                    <Grid item>
                      {isExtension ? (
                        <span
                          style={{
                            color: '#BDBDBD',
                            fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
                            fontSize: '14px',
                            fontStyle: 'normal',
                            fontWeight: 400,
                            lineHeight: '20px',
                            margin: 0,
                            marginTop: '1px',
                            padding: 0,
                          }}
                        >
                          Extension{' '}
                          <span
                            style={{
                              fontFamily: '"Proxima Nova", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                              fontSize: '12px',
                              fontStyle: 'italic',
                              fontWeight: 400,
                              lineHeight: '125%',
                              letterSpacing: '0.06px',
                              color: '#C7C7C7',
                            }}
                          >
                            - coming soon
                          </span>
                        </span>
                      ) : (
                        <span
                          style={{
                            color: 'var(--Neutral-neutral-600, #151618)',
                            fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
                            fontSize: '14px',
                            fontStyle: 'normal',
                            fontWeight: 400,
                            lineHeight: '20px',
                            letterSpacing: '0.07px',
                            margin: 0,
                            marginTop: '1px',
                            padding: 0,
                          }}
                        >
                          {type}
                        </span>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              );
            })}
          </Grid>
        </Grid>

        {/* Integrated Solution filter section */}
        <Grid item style={{ width: '100%' }}>
          <Typography
            variant="body2"
            style={{
              color: 'var(--Neutral-neutral-600, #151618)',
                            fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 700,
              lineHeight: '125%', // 20px
              letterSpacing: '0.5px',
              textAlign: 'left',
              marginBottom: 16,
              marginLeft: 0,
              paddingLeft: 0,
            }}
          >
            Integrated Ellucian Solution
          </Typography>
          <Grid container direction="column" spacing={2} style={{ width: '100%' }}>
            {integratedSolutions.map((solution) => (
              <Grid item key={solution}>
                <Grid container alignItems="center" spacing={1} wrap="nowrap">
                  <Grid item>
                    <Checkbox
                      checked={selectedIntegratedSolutions.includes(solution)}
                      onChange={() => handleIntegratedSolutionChange(solution)}
                      value={solution}
                      inputProps={{ 'aria-label': solution }}
                      style={{ margin: 0, width: 20, height: 20, minWidth: 20, minHeight: 20 }}
                    />
                  </Grid>
                  <Grid item>
                    <span
                      style={{
                        color: 'var(--Neutral-neutral-600, #151618)',
                        fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
                        fontSize: '14px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '20px',
                        letterSpacing: '0.07px',
                        margin: 0,
                        marginTop: '1px',
                        padding: 0,
                      }}
                    >
                      {solution}
                    </span>
                  </Grid>
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Categories filter section */}
        <Grid item>
          <Grid container direction="column" spacing={1}>
            <Grid item style={{ marginBottom: 12 }}>
              <Typography
                variant="body2"
                style={{
                  color: 'var(--Neutral-neutral-600, #151618)',
                        fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  lineHeight: '125%', // 20px
                  letterSpacing: '0.5px',
                  textAlign: 'left',
                }}
              >
                Categories
              </Typography>
            </Grid>

            {/* Category search input */}
            <Grid item>
              <div
                style={{
                  position: 'relative',
                  marginBottom: '8px',
                  ...(window.innerWidth >= 1200 ? { marginBottom: '12px' } : {}),
                }}
              >
                <form className="category-search-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                  }}
                  style={{
                    position: 'relative',
                    width: '100%',
                    /* maxWidth moved to CSS via .category-search-form */
                    borderRadius: '6px',
                    border: '1px solid var(--Neutral-neutral-400, #B2B3B7)',
                    background: '#FFFFFF',
                    display: 'flex',
                    height: '32px',
                    padding: '0 0 0 12px',
                    alignItems: 'center',
                    gap: '6px',
                    boxSizing: 'border-box',
                  }}
                >
                  <input
                    type="text"
                    placeholder="Search by category"
                    value={categorySearchQuery}
                    onChange={handleCategorySearch}
                    style={{
                      flex: 1,
                      height: '100%',
                      padding: '0 32px 0 0',
                      fontFamily: 'Noto Sans',
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      lineHeight: '20px',
                      color: 'var(--Neutral-neutral-600, #151618)',
                      border: 'none',
                      outline: 'none',
                      background: 'transparent',
                      boxSizing: 'border-box',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  />
                  <Icon
                    name="search"
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '14px',
                      height: '14px',
                      color: '#6B7280',
                    }}
                  />
                </form>
              </div>
            </Grid>

            {/* Category checkboxes, show more/less, and empty state */}
            <Grid item>
              <Grid container direction="column" spacing={1} style={{ width: '100%' }}>
                {filteredCategories.length > 0 ? (
                  <>
                    {/* Show limited categories when not searching and showAllCategories is false */}
                    {(categorySearchQuery.trim() === '' && !showAllCategories
                      ? filteredCategories.slice(0, 10)
                      : filteredCategories
                    ).map((category) => (
                      <Grid item key={category} style={{ marginBottom: '8px' }}>
                        <Grid container alignItems="center" spacing={1} wrap="nowrap">
                          <Grid item>
                            <Checkbox
                              checked={selectedCategories.includes(category)}
                              onChange={() => {
                                const isAdding = !selectedCategories.includes(category);
                                if (selectedCategories.includes(category)) {
                                  setSelectedCategories(selectedCategories.filter(c => c !== category));
                                } else {
                                  setSelectedCategories([...selectedCategories, category]);
                                }
                                // Track filter action
                                trackFilter('category', isAdding ? `add_${category}` : `remove_${category}`);
                                
                                // If this looks like an entitlement category, log it
                                if (isAdding && (category.includes('Premium') || category.includes('Standard') || category.includes('Enterprise'))) {
                                  console.log('Entitlement-like category selected:', category);
                                }
                              }}
                              value={category}
                              inputProps={{ 'aria-label': category }}
                              style={{ margin: 0, width: 20, height: 20, minWidth: 20, minHeight: 20 }}
                            />
                          </Grid>
                          <Grid item style={{ flex: 1, minWidth: 0 }}>
                            <span
                              style={{
                                color: 'var(--Neutral-neutral-600, #151618)',
                                fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
                                fontSize: '14px',
                                fontStyle: 'normal',
                                fontWeight: 400,
                                lineHeight: '20px',
                                letterSpacing: '0.07px',
                                margin: 0,
                                marginTop: '1px',
                                padding: 0,
                                cursor: 'pointer',
                                display: 'block',
                                wordBreak: 'break-word',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                              onClick={() => {
                                const isAdding = !selectedCategories.includes(category);
                                if (selectedCategories.includes(category)) {
                                  setSelectedCategories(selectedCategories.filter(c => c !== category));
                                } else {
                                  setSelectedCategories([...selectedCategories, category]);
                                }
                                // Track filter action
                                trackFilter('category', isAdding ? `add_${category}` : `remove_${category}`);
                                
                                // If this looks like an entitlement category, log it
                                if (isAdding && (category.includes('Premium') || category.includes('Standard') || category.includes('Enterprise'))) {
                                  console.log('Entitlement-like category selected:', category);
                                }
                              }}
                            >
                              {category}
                            </span>
                          </Grid>
                        </Grid>
                      </Grid>
                    ))}

                    {/* Show More button when not searching and there are more than 10 categories */}
                    {categorySearchQuery.trim() === '' && filteredCategories.length > 10 && !showAllCategories && (
                      <Grid item style={{ marginTop: '12px' }}>
                        <Typography variant="body3" style={{ fontWeight: 400, fontSize: '14px', lineHeight: '24px', display: 'inline' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>
                              <TextLink
                                role="button"
                                tabIndex={0}
                                underline="always"
                                style={{ color: 'var(--Interactive-Blue-cta-blue-600, #026BC8)', fontWeight: 400, fontSize: '14px', lineHeight: '24px', padding: 0, background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                                onClick={() => setShowAllCategories(true)}
                                onKeyDown={(e: React.KeyboardEvent<HTMLAnchorElement>) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setShowAllCategories(true);
                                  }
                                }}
                              >
                                Show all
                                <Icon name="chevron-down" style={{ width: 12, height: 12, color: 'var(--Interactive-Blue-cta-blue-600, #026BC8)' }} />
                              </TextLink>
                            </span>
                          </span>
                        </Typography>
                      </Grid>
                    )}

                    {/* Show Less button when all categories are shown */}
                    {categorySearchQuery.trim() === '' && filteredCategories.length > 10 && showAllCategories && (
                      <Grid item style={{ marginTop: '12px' }}>
                        <Typography variant="body3" style={{ fontWeight: 400, fontSize: '14px', lineHeight: '24px', display: 'inline' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>
                              <TextLink
                                role="button"
                                tabIndex={0}
                                underline="always"
                                style={{ color: 'var(--Interactive-Blue-cta-blue-600, #026BC8)', fontWeight: 400, fontSize: '14px', lineHeight: '24px', padding: 0, background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                                onClick={() => setShowAllCategories(false)}
                                onKeyDown={(e: React.KeyboardEvent<HTMLAnchorElement>) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setShowAllCategories(false);
                                  }
                                }}
                              >
                                Show less
                                <Icon name="chevron-up" style={{ width: 12, height: 12, color: 'var(--Interactive-Blue-cta-blue-600, #026BC8)' }} />
                              </TextLink>
                            </span>
                          </span>
                        </Typography>
                      </Grid>
                    )}
                  </>
                ) : (
                  // Empty state: no categories found
                  <Grid item>
                    <Typography
                      style={{
                        color: '#9CA3AF',
                        fontFamily: 'Noto Sans, sans-serif',
                        fontSize: '14px',
                        fontStyle: 'italic',
                        textAlign: 'center',
                        padding: '16px 0',
                      }}
                    >
                      No categories found
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        
      </Grid>

      </div>
  );
};

export default FilterSidebar;